import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminDashboardData } from '@/mocks/adminDashboard';
import TransmissionStatusCard from './components/TransmissionStatusCard';
import WidgetsGrid from './components/WidgetsGrid';

type LoadState = 'loading' | 'error' | 'ready';

// ── helpers ──
function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

// ── Quick action definition ──
interface QuickActionDef {
  label: string;
  icon: string;
  path: string;
  bg: string;
  iconColor: string;
}

export default function AdminDashboard() {
  const [loadState, setLoadState] = useState<LoadState>('loading');

  useEffect(() => {
    const timer = setTimeout(() => setLoadState('ready'), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setLoadState('loading');
    setTimeout(() => setLoadState('ready'), 400);
  }, []);

  // ── Error State ──
  if (loadState === 'error') {
    return (
      <div className="min-h-screen bg-background-100 flex items-center justify-center p-4">
        <div className="bg-background-50 border border-background-200 rounded-2xl p-8 max-w-sm w-full text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h2 className="text-foreground-950 font-bold text-base mb-1.5">Error cargando dashboard</h2>
          <p className="text-foreground-400 text-xs mb-5">
            No se pudieron cargar los datos de tu panel. Verificá tu conexión e intentá de nuevo.
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm"></i>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const { station, frequency, plan, license, domain, social, upcomingEvents, radioScore } = adminDashboardData;
  const chartData = adminDashboardData.listenersLast7Days;
  const maxListeners = Math.max(...chartData.map((d) => d.listeners));
  const activities = adminDashboardData.recentActivity.slice(0, 8);

  // ── Quick actions ──
  const quickActions: QuickActionDef[] = [
    {
      label: 'Transmitir',
      icon: 'ri-broadcast-line',
      path: '/admin/broadcast-center',
      bg: 'bg-red-50 hover:bg-red-100',
      iconColor: 'text-red-600',
    },
    {
      label: 'Crear publicación',
      icon: 'ri-article-line',
      path: '/admin/posts',
      bg: 'bg-sky-50 hover:bg-sky-100',
      iconColor: 'text-sky-600',
    },
    {
      label: 'Crear evento',
      icon: 'ri-calendar-event-line',
      path: '/admin/events',
      bg: 'bg-violet-50 hover:bg-violet-100',
      iconColor: 'text-violet-600',
    },
    {
      label: 'Crear concurso',
      icon: 'ri-trophy-line',
      path: '/admin/contests',
      bg: 'bg-amber-50 hover:bg-amber-100',
      iconColor: 'text-amber-600',
    },
    {
      label: 'Ver comunidad',
      icon: 'ri-group-line',
      path: '/admin/community',
      bg: 'bg-emerald-50 hover:bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      label: 'Ver monetización',
      icon: 'ri-money-dollar-circle-line',
      path: '/admin/monetization',
      bg: 'bg-primary-50 hover:bg-primary-100',
      iconColor: 'text-primary-600',
    },
  ];

  // ── Activity type config ──
  const activityIconMap: Record<string, string> = {
    foto: 'ri-image-line',
    mensaje: 'ri-chat-3-line',
    follower: 'ri-user-add-line',
    comentario: 'ri-message-2-line',
    live: 'ri-live-line',
    evento: 'ri-calendar-event-line',
    publicacion: 'ri-article-line',
    video: 'ri-video-line',
  };

  const activityColorMap: Record<string, string> = {
    foto: 'bg-blue-100 text-blue-500',
    mensaje: 'bg-purple-100 text-purple-500',
    follower: 'bg-accent-100 text-accent-600',
    comentario: 'bg-amber-100 text-amber-500',
    live: 'bg-rose-100 text-rose-500',
    evento: 'bg-emerald-100 text-emerald-500',
    publicacion: 'bg-cyan-100 text-cyan-500',
    video: 'bg-indigo-100 text-indigo-500',
  };

  return (
    <>
      {/* ── Mobile topbar ── */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5 pl-12">
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Dashboard</h1>
              <p className="text-foreground-400 text-[9px]">Panel de control</p>
            </div>
          </div>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">
        {/* ── Main content ── */}
        <div className="flex-1 min-w-0">
          {/* ── Desktop toolbar ── */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Dashboard</h2>
              <p className="text-foreground-400 text-xs">
                Bienvenido de vuelta, Carlos. Tu estación está al aire.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
              <Link
                to="/admin/settings"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-settings-3-line text-xs"></i>
                Ajustes
              </Link>
            </div>
          </div>

          {/* ── Page content ── */}
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">

            {/* ══════ WIDGETS GRID ══════ */}
            <WidgetsGrid
              stats={loadState === 'ready' ? adminDashboardData.stats : null}
              frequency={loadState === 'ready' ? frequency : null}
              plan={loadState === 'ready' ? plan : null}
              license={loadState === 'ready' ? license : null}
              domain={loadState === 'ready' ? domain : null}
              social={loadState === 'ready' ? social : null}
              upcomingEvents={loadState === 'ready' ? upcomingEvents : []}
              station={loadState === 'ready' ? station : null}
              radioScore={loadState === 'ready' ? radioScore : null}
              loading={loadState === 'loading'}
              error={loadState === 'error'}
              onRetry={handleRetry}
            />

            {/* ══════ TRANSMISSION STATUS ══════ */}
            {loadState !== 'loading' && (
              <TransmissionStatusCard transmission={adminDashboardData.transmission} />
            )}

            {/* ══════ CHART + ACTIVITY ══════ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* ── Weekly listeners chart ── */}
              <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-foreground-950 font-semibold text-sm">Oyentes esta semana</h3>
                    <p className="text-foreground-400 text-[10px] mt-0.5">Reproducciones diarias · últimos 7 días</p>
                  </div>
                  <span className="text-foreground-400 text-[10px] bg-background-100 px-2 py-0.5 rounded-full">
                    Actualizado hoy
                  </span>
                </div>

                {loadState === 'loading' ? (
                  <div className="flex items-end gap-3 h-[180px]">
                    {Array.from({ length: 7 }).map((_, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-2 animate-pulse">
                        <div className="w-full bg-background-200 rounded-t-lg" style={{ height: `${40 + Math.random() * 60}%` }}></div>
                        <div className="w-6 h-3 bg-background-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    {/* Bars */}
                    <div className="flex items-end gap-2 md:gap-3 h-[180px] px-1">
                      {chartData.map((d, i) => {
                        const pct = (d.listeners / maxListeners) * 100;
                        return (
                          <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                            <span className="text-foreground-400 text-[9px] font-medium">
                              {fmt(d.listeners)}
                            </span>
                            <div className="w-full rounded-t-lg transition-all duration-500 ease-out bg-primary-500/80 hover:bg-primary-500 cursor-pointer"
                              style={{
                                height: `${Math.max(pct, 4)}%`,
                                animation: `fadeInUp 0.4s ease-out forwards`,
                                animationDelay: `${i * 60}ms`,
                                opacity: 0,
                              }}
                            ></div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Day labels */}
                    <div className="flex items-center gap-2 md:gap-3 mt-2 px-1">
                      {chartData.map((d) => (
                        <div key={d.day} className="flex-1 text-center">
                          <span className="text-foreground-400 text-[10px] font-medium">{d.day}</span>
                        </div>
                      ))}
                    </div>
                    {/* Summary */}
                    <div className="flex items-center gap-4 mt-4 pt-4 border-t border-background-200">
                      <div className="flex items-center gap-1.5">
                        <span className="text-foreground-400 text-[10px]">Total semana:</span>
                        <span className="text-foreground-950 text-xs font-bold">
                          {fmt(chartData.reduce((s, d) => s + d.listeners, 0))}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-foreground-400 text-[10px]">Promedio diario:</span>
                        <span className="text-foreground-950 text-xs font-bold">
                          {fmt(Math.round(chartData.reduce((s, d) => s + d.listeners, 0) / 7))}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-foreground-400 text-[10px]">Mejor día:</span>
                        <span className="text-emerald-600 text-xs font-bold">
                          {chartData.reduce((best, d) => d.listeners > best.listeners ? d : best).day}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Recent Activity ── */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-foreground-950 font-semibold text-sm">Actividad reciente</h3>
                  <span className="text-foreground-400 text-[10px] bg-background-100 px-2 py-0.5 rounded-full">
                    Últimas {activities.length}
                  </span>
                </div>

                {loadState === 'loading' ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 animate-pulse">
                        <div className="w-8 h-8 rounded-full bg-background-200 shrink-0"></div>
                        <div className="flex-1 space-y-1.5">
                          <div className="w-3/4 h-3 bg-background-200 rounded"></div>
                          <div className="w-1/2 h-2.5 bg-background-200 rounded"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {activities.map((act, idx) => {
                      const iconColor = activityColorMap[act.type] || 'bg-background-200 text-foreground-400';
                      return (
                        <div
                          key={act.id}
                          className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                          style={{
                            animation: `fadeIn 0.25s ease-out forwards`,
                            animationDelay: `${idx * 40}ms`,
                            opacity: 0,
                          }}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${iconColor}`}>
                            <i className={`${activityIconMap[act.type] || 'ri-article-line'} text-xs`}></i>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-foreground-800 text-xs font-medium leading-tight">
                              {act.description}
                            </p>
                            <p className="text-foreground-500 text-[10px] leading-tight mt-0.5 line-clamp-1">
                              {act.detail}
                            </p>
                            <span className="text-foreground-400 text-[9px] mt-1 block">{act.time}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ══════ QUICK ACCESS ══════ */}
            <div>
              <h3 className="text-foreground-950 font-semibold text-sm mb-3">Accesos rápidos</h3>
              {loadState === 'loading' ? (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex flex-col items-center gap-2 animate-pulse">
                      <div className="w-10 h-10 rounded-xl bg-background-200"></div>
                      <div className="w-16 h-3 bg-background-200 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {quickActions.map((action, idx) => (
                    <Link
                      key={action.label}
                      to={action.path}
                      className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border border-background-200 transition-all cursor-pointer group ${action.bg}`}
                      style={{
                        animation: `fadeInUp 0.3s ease-out forwards`,
                        animationDelay: `${idx * 50}ms`,
                        opacity: 0,
                      }}
                    >
                      <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <i className={`${action.icon} ${action.iconColor} text-lg`}></i>
                      </div>
                      <span className="text-foreground-700 text-[10px] font-semibold text-center leading-tight whitespace-nowrap">
                        {action.label}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>


    </>
  );
}